import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import Select from "@8x8/oxygen-select";

import {
  customerSelector,
  tenantIdSelector,
  pbxIdSelector,
} from "@/react/features/customer/customerSlice";
import { CopyButton } from "@/react/components/CopyButton";
import {
  CustomerCardContainer,
  TopContainer,
  BottomContainer,
  DropdownWrapper,
  SelectorLabel,
} from "./styled";
import { DivWithMargin } from "@/react/components/styled";
import useCustomerSelectModal from "./useCustomerSelectModal";
import useFallback from "./useFallback";
import useHandlers from "./useHandlers";

const tenantToOption = (tenant) => ({
  value: tenant.tenantId,
  label: `${tenant.tenantId} (${tenant.clusterId})`,
});

const pbxToOption = (pbx) => ({
  value: pbx.id,
  label: pbx.name,
});

const findOptionById = (options, id) =>
  options.find((option) => option.value === id);

const CustomerCard = ({ hasPbxSelector }) => {
  const customer = useSelector(customerSelector);
  const customerName = customer?.customerName;
  const cid = customer?.cid;
  const tenants = customer?.tenantInfo ?? [];
  const pbxes = customer?.pbxList ?? [];

  const tenantId = useSelector(tenantIdSelector);
  const pbxId = useSelector(pbxIdSelector);

  const _openCustomerSelectModal = useCustomerSelectModal();
  const { onChangePbx, onChangeTenant } = useHandlers();

  const tenantOptions = tenants.map(tenantToOption);
  const pbxOptions = pbxes.map(pbxToOption);

  useFallback(tenants, tenantId, "tenantId", onChangeTenant);
  useFallback(pbxes, pbxId, "id", onChangePbx);
  useEffect(() => {
    if (!customerName) {
      _openCustomerSelectModal();
    }
  }, [customerName]);

  return (
    <CustomerCardContainer>
      <TopContainer
        onClick={_openCustomerSelectModal}
        data-test-id={"customer-selector-details"}
        role="button"
        tabIndex="0"
      >
        <SelectorLabel>Customer:</SelectorLabel>
        <DivWithMargin marginLeft={8} marginRight={"auto"}>
          {customerName || "Not Selected"}
        </DivWithMargin>
        {cid && (
          <>
            <DivWithMargin marginLeft={16}>
              <SelectorLabel>Id:</SelectorLabel>
            </DivWithMargin>
            <DivWithMargin marginLeft={16}>{cid}</DivWithMargin>
          </>
        )}
      </TopContainer>
      <BottomContainer tabIndex="1">
        <SelectorLabel>Tenant:</SelectorLabel>
        <DropdownWrapper>
          <Select
            testId={"tenant-select"}
            value={findOptionById(tenantOptions, tenantId)}
            options={tenantOptions}
            onChange={onChangeTenant}
            isClearable={false}
          />
        </DropdownWrapper>
        <DivWithMargin marginLeft={"16px"}>
          <CopyButton value={tenantId} />
        </DivWithMargin>
        {hasPbxSelector && (
          <>
            <DivWithMargin marginLeft={16}>
              <SelectorLabel>Pbx:</SelectorLabel>
            </DivWithMargin>
            <DropdownWrapper>
              <Select
                testId={"pbx-select"}
                value={findOptionById(pbxOptions, pbxId)}
                options={pbxOptions}
                onChange={onChangePbx}
                isClearable={false}
              />
            </DropdownWrapper>
            <DivWithMargin marginLeft={16}>
              <CopyButton value={pbxId} />
            </DivWithMargin>
          </>
        )}
      </BottomContainer>
    </CustomerCardContainer>
  );
};

export default CustomerCard;
